import React from 'react';
import { X, User, Mail, Phone, MapPin, Calendar, DollarSign, FileText, Key } from 'lucide-react';

const MemberDetailsModal = ({ member, onClose }) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'Not provided';
    return new Date(dateString).toLocaleDateString();
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'ADMIN': return 'bg-red-100 text-red-800';
      case 'TECHNICIAN': return 'bg-blue-100 text-blue-800';
      case 'RECEPTIONIST': return 'bg-green-100 text-green-800';
      case 'STOREKEEPER': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Member Details</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Profile Section */}
          <div className="lg:col-span-1">
            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <div className="w-24 h-24 bg-gray-300 rounded-full mx-auto mb-4 flex items-center justify-center">
                {member.profilePhoto ? (
                  <img
                    src={member.profilePhoto}
                    alt={member.name}
                    className="w-24 h-24 rounded-full object-cover"
                  />
                ) : (
                  <User className="w-12 h-12 text-gray-600" />
                )}
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-2">{member.name}</h4>
              <span className={`px-3 py-1 text-sm rounded-full ${getRoleBadgeColor(member.role)}`}>
                {member.role}
              </span>
              <div className="mt-4 space-y-2">
                <div className={`px-3 py-1 text-sm rounded-full ${
                  member.isActive 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {member.isActive ? 'Active' : 'Inactive'}
                </div>
                <div className={`px-3 py-1 text-sm rounded-full ${
                  member.canLogin 
                    ? 'bg-blue-100 text-blue-800' 
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  Login: {member.canLogin ? 'Enabled' : 'Disabled'}
                </div>
              </div>
            </div>
          </div>

          {/* Details Section */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <div className="bg-white border rounded-lg p-4">
              <h5 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center">
                  <User className="w-4 h-4 text-gray-400 mr-2" />
                  <div>
                    <p className="text-sm text-gray-500">Employee ID</p>
                    <p className="text-sm font-medium text-gray-900">{member.employeeId || 'Not assigned'}</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <Mail className="w-4 h-4 text-gray-400 mr-2" />
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="text-sm font-medium text-gray-900">{member.email}</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <Phone className="w-4 h-4 text-gray-400 mr-2" />
                  <div>
                    <p className="text-sm text-gray-500">Phone</p>
                    <p className="text-sm font-medium text-gray-900">{member.phone || 'Not provided'}</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <MapPin className="w-4 h-4 text-gray-400 mr-2" />
                  <div>
                    <p className="text-sm text-gray-500">Branch</p>
                    <p className="text-sm font-medium text-gray-900">{member.branch?.name || 'Not assigned'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Employment Details */}
            <div className="bg-white border rounded-lg p-4">
              <h5 className="text-lg font-medium text-gray-900 mb-4">Employment Details</h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center">
                  <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                  <div>
                    <p className="text-sm text-gray-500">Date of Birth</p>
                    <p className="text-sm font-medium text-gray-900">{formatDate(member.dateOfBirth)}</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                  <div>
                    <p className="text-sm text-gray-500">Hire Date</p>
                    <p className="text-sm font-medium text-gray-900">{formatDate(member.hireDate)}</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <DollarSign className="w-4 h-4 text-gray-400 mr-2" />
                  <div>
                    <p className="text-sm text-gray-500">Salary</p>
                    <p className="text-sm font-medium text-gray-900">
                      {member.salary ? `$${member.salary}` : 'Not specified'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center">
                  <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                  <div>
                    <p className="text-sm text-gray-500">Member Since</p>
                    <p className="text-sm font-medium text-gray-900">{formatDate(member.createdAt)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Documents */}
            <div className="bg-white border rounded-lg p-4">
              <h5 className="text-lg font-medium text-gray-900 mb-4">Documents & IDs</h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center">
                  <FileText className="w-4 h-4 text-gray-400 mr-2" />
                  <div>
                    <p className="text-sm text-gray-500">Emirates ID</p>
                    <p className="text-sm font-medium text-gray-900">{member.emiratesId || 'Not provided'}</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <Key className="w-4 h-4 text-gray-400 mr-2" />
                  <div>
                    <p className="text-sm text-gray-500">License Number</p>
                    <p className="text-sm font-medium text-gray-900">{member.licenseNumber || 'Not provided'}</p>
                  </div>
                </div>
              </div>
              
              {/* Document Links */}
              <div className="mt-4 space-y-3">
                {member.emiratesIdDocument && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm font-medium text-gray-700 mb-2">Emirates ID Document</p>
                    <div className="flex gap-2">
                      <a
                        href={member.emiratesIdDocument}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                      >
                        <FileText className="w-4 h-4 mr-1" />
                        View
                      </a>
                      <a
                        href={member.emiratesIdDocument}
                        download
                        className="inline-flex items-center px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                      >
                        Download
                      </a>
                    </div>
                  </div>
                )}
                {member.licenseDocument && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm font-medium text-gray-700 mb-2">License Document</p>
                    <div className="flex gap-2">
                      <a
                        href={member.licenseDocument}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                      >
                        <FileText className="w-4 h-4 mr-1" />
                        View
                      </a>
                      <a
                        href={member.licenseDocument}
                        download
                        className="inline-flex items-center px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                      >
                        Download
                      </a>
                    </div>
                  </div>
                )}
                {!member.emiratesIdDocument && !member.licenseDocument && (
                  <p className="text-sm text-gray-500 italic">No documents uploaded</p>
                )}
              </div>
            </div>

            {/* Address */}
            {member.address && (
              <div className="bg-white border rounded-lg p-4">
                <h5 className="text-lg font-medium text-gray-900 mb-4">Address</h5>
                <div className="flex items-start">
                  <MapPin className="w-4 h-4 text-gray-400 mr-2 mt-1" />
                  <p className="text-sm text-gray-900">{member.address}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default MemberDetailsModal;